import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { TablePagination } from "@/components/common/TablePagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebounce } from "@/hooks/useDebounce";
import type {
  AdminTenantSubscriptionOverviewFilters,
  AdminTenantSubscriptionOverviewItem,
  AdminTenantSubscriptionStatus,
} from "@/types/tenant-subscription";
import { useAdminTenantSubscriptions } from "../hooks/useAdminTenantSubscriptions";

const DEFAULT_PAGE_SIZE = 20;

const STATUS_OPTIONS: AdminTenantSubscriptionStatus[] = [
  "ACTIVE",
  "TRIALING",
  "PAST_DUE",
  "CANCELED",
  "INCOMPLETE",
  "INCOMPLETE_EXPIRED",
  "UNPAID",
  "PAUSED",
];

type SubscriptionOverviewTabProps = {
  enabled: boolean;
};

type SubscriptionSortField =
  | "tenant"
  | "hotel"
  | "plan"
  | "status"
  | "billingQuantity"
  | "currentPeriodEnd"
  | "createdAt";
type SortDirection = "asc" | "desc" | null;

function formatDate(value: string | null): string {
  return value ? format(new Date(value), "dd MMM yyyy") : "—";
}

function getStatusVariant(status: string) {
  switch (status) {
    case "ACTIVE":
    case "TRIALING":
      return "default";
    case "PAST_DUE":
    case "UNPAID":
      return "destructive";
    case "CANCELED":
    case "PAUSED":
      return "secondary";
    default:
      return "outline";
  }
}

export const SubscriptionOverviewTab: React.FC<
  SubscriptionOverviewTabProps
> = ({ enabled }) => {
  const { t } = useTranslation();
  const [tenantName, setTenantName] = useState("");
  const [hotelName, setHotelName] = useState("");
  const [planTitle, setPlanTitle] = useState("");
  const [status, setStatus] = useState<AdminTenantSubscriptionStatus | "all">(
    "all",
  );
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortField, setSortField] =
    useState<SubscriptionSortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const debouncedTenantName = useDebounce(tenantName, 300);
  const debouncedHotelName = useDebounce(hotelName, 300);
  const debouncedPlanTitle = useDebounce(planTitle, 300);

  const filters = useMemo((): AdminTenantSubscriptionOverviewFilters => {
    const offset = (currentPage - 1) * pageSize;

    return {
      tenantName: debouncedTenantName.trim() || undefined,
      hotelName: debouncedHotelName.trim() || undefined,
      planTitle: debouncedPlanTitle.trim() || undefined,
      status: status === "all" ? undefined : status,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
      limit: pageSize,
      offset,
    };
  }, [
    createdFrom,
    createdTo,
    currentPage,
    debouncedHotelName,
    debouncedPlanTitle,
    debouncedTenantName,
    pageSize,
    status,
  ]);

  const { data, isLoading } = useAdminTenantSubscriptions(enabled, filters);

  const subscriptions = useMemo(() => {
    const rows = [...(data?.subscriptions ?? [])];

    if (sortDirection === null) {
      return rows;
    }

    const getSortValue = (
      row: AdminTenantSubscriptionOverviewItem,
    ): number | string => {
      switch (sortField) {
        case "tenant":
          return row.tenant.name.toLowerCase();
        case "hotel":
          return (row.hotel?.name ?? "").toLowerCase();
        case "plan":
          return row.plan.title.toLowerCase();
        case "status":
          return row.status;
        case "billingQuantity":
          return row.billingQuantity;
        case "currentPeriodEnd":
          return row.currentPeriodEnd
            ? new Date(row.currentPeriodEnd).getTime()
            : 0;
        case "createdAt":
        default:
          return new Date(row.createdAt).getTime();
      }
    };

    rows.sort((left, right) => {
      const leftValue = getSortValue(left);
      const rightValue = getSortValue(right);

      if (leftValue < rightValue) {
        return sortDirection === "asc" ? -1 : 1;
      }
      if (leftValue > rightValue) {
        return sortDirection === "asc" ? 1 : -1;
      }
      return 0;
    });

    return rows;
  }, [data?.subscriptions, sortDirection, sortField]);

  const total = data?.total ?? 0;
  const responsePageSize = data?.limit ?? pageSize;
  const totalPages = Math.max(1, Math.ceil(total / responsePageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const hasActiveFilters =
    tenantName.trim() !== "" ||
    hotelName.trim() !== "" ||
    planTitle.trim() !== "" ||
    status !== "all" ||
    createdFrom !== "" ||
    createdTo !== "";

  const handleResetFilters = () => {
    setTenantName("");
    setHotelName("");
    setPlanTitle("");
    setStatus("all");
    setCreatedFrom("");
    setCreatedTo("");
    setCurrentPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setSortField("createdAt");
    setSortDirection("desc");
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setCurrentPage(1);
  };

  const columns: Column<AdminTenantSubscriptionOverviewItem>[] = useMemo(
    () => [
      {
        key: "tenant",
        header: t("subscriptions.overview.table.tenant"),
        sortable: true,
        render: (_: unknown, row) => (
          <div className="min-w-[160px]">
            <p className="truncate font-medium text-zinc-950">
              {row.tenant.name}
            </p>
            <p className="text-muted-foreground truncate text-xs">
              {row.tenant.slug}
            </p>
          </div>
        ),
      },
      {
        key: "hotel",
        header: t("subscriptions.overview.table.hotel"),
        sortable: true,
        render: (_: unknown, row) => (
          <span className="line-clamp-2 min-w-[140px]">
            {row.hotel?.name ?? t("subscriptions.overview.table.tenantWide")}
          </span>
        ),
      },
      {
        key: "plan",
        header: t("subscriptions.overview.table.plan"),
        sortable: true,
        render: (_: unknown, row) => (
          <span className="line-clamp-2 min-w-[140px]">{row.plan.title}</span>
        ),
      },
      {
        key: "status",
        header: t("subscriptions.overview.table.status"),
        className: "text-center",
        sortable: true,
        render: (value: string) => (
          <Badge variant={getStatusVariant(value)}>{value}</Badge>
        ),
      },
      {
        key: "billingQuantity",
        header: t("subscriptions.overview.table.quantity"),
        className: "text-right",
        sortable: true,
      },
      {
        key: "currentPeriodEnd",
        header: t("subscriptions.overview.table.periodEnd"),
        sortable: true,
        render: (value: string | null) => formatDate(value),
      },
      {
        key: "cancelAtPeriodEnd",
        header: t("subscriptions.overview.table.cancelAtPeriodEnd"),
        className: "text-center",
        render: (value: boolean) =>
          value ? t("subscriptions.plans.yes") : t("subscriptions.plans.no"),
      },
      {
        key: "stripeSubscriptionId",
        header: t("subscriptions.overview.table.stripeId"),
        render: (value: string | null) => (
          <span className="line-clamp-1 max-w-[180px] text-xs">
            {value ?? "—"}
          </span>
        ),
      },
      {
        key: "createdAt",
        header: t("subscriptions.overview.table.createdAt"),
        sortable: true,
        render: (value: string) => format(new Date(value), "dd MMM yyyy"),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          {t("subscriptions.overview.summary.total", { count: total })}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={handleResetFilters}
          disabled={!hasActiveFilters}
        >
          {t("subscriptions.overview.filters.reset")}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Input
          label={t("subscriptions.overview.filters.tenantName")}
          value={tenantName}
          onChange={(event) => {
            setTenantName(event.target.value);
            setCurrentPage(1);
          }}
        />
        <Input
          label={t("subscriptions.overview.filters.hotelName")}
          value={hotelName}
          onChange={(event) => {
            setHotelName(event.target.value);
            setCurrentPage(1);
          }}
        />
        <Input
          label={t("subscriptions.overview.filters.planTitle")}
          value={planTitle}
          onChange={(event) => {
            setPlanTitle(event.target.value);
            setCurrentPage(1);
          }}
        />
        <div className="flex flex-col gap-1.5">
          <span className="text-sm leading-none font-medium text-zinc-900">
            {t("subscriptions.overview.filters.status")}
          </span>
          <Select
            value={status}
            onValueChange={(value) => {
              setStatus(value as AdminTenantSubscriptionStatus | "all");
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("subscriptions.overview.filters.allStatuses")}
              </SelectItem>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          type="datetime-local"
          label={t("subscriptions.overview.filters.createdFrom")}
          value={createdFrom}
          onChange={(event) => {
            setCreatedFrom(event.target.value);
            setCurrentPage(1);
          }}
        />
        <Input
          type="datetime-local"
          label={t("subscriptions.overview.filters.createdTo")}
          value={createdTo}
          onChange={(event) => {
            setCreatedTo(event.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <DataTable<AdminTenantSubscriptionOverviewItem>
        className="overflow-x-auto"
        tableClassName="min-w-[1120px]"
        data={subscriptions}
        columns={columns}
        loading={isLoading}
        withPagination={false}
        sorting={{
          field: sortField,
          direction: sortDirection,
          onSort: (field) => {
            const nextField = field as SubscriptionSortField;

            if (sortField !== nextField) {
              setSortField(nextField);
              setSortDirection("asc");
              return;
            }

            setSortDirection((currentDirection) => {
              if (currentDirection === "asc") {
                return "desc";
              }
              if (currentDirection === "desc") {
                return null;
              }
              return "asc";
            });
          },
        }}
        emptyMessage={t("subscriptions.overview.table.empty")}
      />

      <TablePagination
        page={currentPage}
        pageSize={responsePageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};
